/* eslint-disable max-lines */
import { useNeighbourAlarms } from '@hakimo-ui/hakimo/data-access';
import { NeighbourAlarmsResponse } from '@hakimo-ui/hakimo/types';
import { useUser } from '@hakimo-ui/hakimo/util';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useAlarmGroupPlayback,
  useAlarmGroupStatic,
} from '../alarm-provider/AlarmGroupProvider';
import { ZOOM_CONFIG } from '../constants';
import { getNeighborAlarmsSearchParams } from './utils';

export interface UseAutoLoadNeighbouringAlarmsReturn {
  requestExtension: (pastMs: number, futureMs: number) => void;
  isFetching: boolean;
}

export function useAutoLoadNeighbouringAlarms(): UseAutoLoadNeighbouringAlarmsReturn {
  const user = useUser();
  const {
    neighbourSourceEntityIds,
    location,
    product,
    isScanLike,
    dataVersion,
  } = useAlarmGroupStatic();
  const {
    timelineBounds: {
      totalTimelineStartTime: ts,
      totalTimelineEndTime: te,
      alarmGroupStartTime: ags,
      alarmGroupEndTime: age,
    },
    handleNeighbourAlarmsResponse,
  } = useAlarmGroupPlayback();

  const [isFetching, setIsFetching] = useState(false);
  const isFetchingRef = useRef(false);
  const pendingPastRef = useRef(0);
  const pendingFutureRef = useRef(0);
  const pendingResponsesRef = useRef(0);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onNeighbourAlarmsResponse = useCallback(
    (data: NeighbourAlarmsResponse) => {
      handleNeighbourAlarmsResponse(data);
      if (pendingResponsesRef.current > 0) {
        pendingResponsesRef.current -= 1;
        if (pendingResponsesRef.current === 0) {
          isFetchingRef.current = false;
          setIsFetching(false);
        }
      }
    },
    [handleNeighbourAlarmsResponse]
  );

  const fetchNeighbourAlarmsApi = useNeighbourAlarms(
    onNeighbourAlarmsResponse,
    dataVersion
  );

  const pastHeadroom = Math.max(
    0,
    ts - (ags - ZOOM_CONFIG.MAX_NEIGHBOUR_OFFSET_MS)
  );
  const futureHeadroom = Math.max(
    0,
    age + ZOOM_CONFIG.MAX_NEIGHBOUR_OFFSET_MS - te
  );

  const fireFetches = useCallback(() => {
    if (isFetchingRef.current) return;

    const pastPerSide = Math.min(pendingPastRef.current, pastHeadroom);
    const futurePerSide = Math.min(pendingFutureRef.current, futureHeadroom);
    pendingPastRef.current = 0;
    pendingFutureRef.current = 0;

    if (pastPerSide <= 0 && futurePerSide <= 0) return;

    const statusParam = isScanLike ? undefined : 'All';

    let expectedResponses = 0;
    if (pastPerSide > 0) {
      const prevParams = getNeighborAlarmsSearchParams(
        Math.floor(ts - pastPerSide - 1000),
        Math.floor(ts - 1000),
        neighbourSourceEntityIds,
        location?.id,
        statusParam,
        user.email,
        product
      );
      fetchNeighbourAlarmsApi(prevParams);
      expectedResponses += 1;
    }
    if (futurePerSide > 0) {
      const nextParams = getNeighborAlarmsSearchParams(
        Math.floor(te + 1000),
        Math.floor(te + futurePerSide),
        neighbourSourceEntityIds,
        location?.id,
        statusParam,
        user.email,
        product
      );
      fetchNeighbourAlarmsApi(nextParams);
      expectedResponses += 1;
    }

    if (expectedResponses === 0) return;

    pendingResponsesRef.current = expectedResponses;
    isFetchingRef.current = true;
    setIsFetching(true);
  }, [
    neighbourSourceEntityIds,
    fetchNeighbourAlarmsApi,
    futureHeadroom,
    // isScanLike is derived from the group's media, which resolves after the
    // first render — omitting it here pinned statusParam to its pre-load value.
    isScanLike,
    location?.id,
    pastHeadroom,
    product,
    te,
    ts,
    user.email,
  ]);

  const requestExtension = useCallback(
    (pastMs: number, futureMs: number) => {
      if (isFetchingRef.current) return;
      if (pastHeadroom <= 0 && futureHeadroom <= 0) return;
      pendingPastRef.current = pastMs;
      pendingFutureRef.current = futureMs;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        debounceTimeoutRef.current = null;
        fireFetches();
      }, ZOOM_CONFIG.AUTO_LOAD_FETCH_DEBOUNCE_MS);
    },
    [fireFetches, futureHeadroom, pastHeadroom]
  );

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return { requestExtension, isFetching };
}

export default useAutoLoadNeighbouringAlarms;
